import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {Alert, App, Button, Card, ConfigProvider, Empty, InputNumber, Popconfirm, Space, Switch, Table, Tag, Tooltip, Typography, theme} from "antd";
import {DeleteOutlined, HistoryOutlined, LockOutlined, QuestionCircleOutlined, SaveOutlined} from "@ant-design/icons";
import AxiosServices from "../../Services/axiosServices";
import {HasMatchStarted, IMatch} from "../../Interfaces/IMatch";
import {formatMatchDateTime, matchAsUtcDate} from "../../Services/datetime";
import {useAuth} from "../../config/AuthContext";
import {useGlobal} from "../../config/GlobalContext";

const ACCENT = '#203923';
const {Paragraph} = Typography;

type Side = 'DOM' | 'EXT';

type ScoreCellProps = {
    match: IMatch;
    side: Side;
    saving: boolean;
    disabled: boolean;
    onSave: (match: IMatch, side: Side, value: number) => Promise<void> | void;
};

const ScoreCell: FC<ScoreCellProps> = ({match, side, saving, disabled, onSave}) => {
    const initial = side === 'DOM' ? match.Score_Dom : match.Score_Ext;
    const initialValue = (initial ?? -1) === -1 ? null : (initial as number);
    const [value, setValue] = useState<number | null>(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    return (
        <InputNumber
            min={0}
            max={20}
            value={value}
            onChange={(v) => setValue(v as number | null)}
            onBlur={() => {
                if (value === null || value === undefined) return;
                if (value === initialValue) return;
                onSave(match, side, value);
            }}
            disabled={saving || disabled}
            style={{width: 72}}
        />
    );
};

const AdminScores: FC = () => {
    const {isAdmin, isLoggedIn} = useAuth();
    const {currentCmpEdt} = useGlobal();
    const {message} = App.useApp();

    const [matches, setMatches] = useState<IMatch[]>([]);
    const [loading, setLoading] = useState(false);
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [deletingIdt, setDeletingIdt] = useState<number | null>(null);
    const [showOld, setShowOld] = useState(false);
    const [showNoScore, setShowNoScore] = useState(false);
    const [hideFinished, setHideFinished] = useState(false);
    const [demoMode, setDemoMode] = useState(false);
    const [togglingFinishedIdt, setTogglingFinishedIdt] = useState<number | null>(null);

    const refresh = useCallback(() => {
        if (!currentCmpEdt) return;
        setLoading(true);
        AxiosServices.getMatches(setMatches, currentCmpEdt)
            .catch(() => message.error("Impossible de charger les matches."))
            .finally(() => setLoading(false));
    }, [currentCmpEdt, message]);

    useEffect(() => {
        if (!isAdmin || !currentCmpEdt) return;
        refresh();
    }, [isAdmin, currentCmpEdt, refresh]);

    const sortedMatches = useMemo(() => {
        return [...matches].sort((a, b) => {
            const ta = matchAsUtcDate(a.Dte, a.Hre)?.getTime() ?? Number.POSITIVE_INFINITY;
            const tb = matchAsUtcDate(b.Dte, b.Hre)?.getTime() ?? Number.POSITIVE_INFINITY;
            return ta - tb || (a.Num ?? 0) - (b.Num ?? 0);
        });
    }, [matches]);

    const filteredMatches = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return sortedMatches.filter(m => {
            const matchDate = matchAsUtcDate(m.Dte, m.Hre);
            const isOld = matchDate ? matchDate.getTime() < today.getTime() : false;
            const hasScore = (m.Score_Dom ?? -1) !== -1 && (m.Score_Ext ?? -1) !== -1;
            const isFinished = !!Number(m.finished ?? 0);
            if (!showOld && isOld) return false;
            if (showNoScore && hasScore) return false;
            if (hideFinished && isFinished) return false;
            return true;
        });
    }, [sortedMatches, showOld, showNoScore, hideFinished]);

    if (!isLoggedIn) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Connecte-toi pour accéder à cette page."/>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div style={{padding: 24}}>
                <Alert
                    type="error"
                    showIcon
                    icon={<LockOutlined/>}
                    message="Accès refusé"
                    description="Cette page est réservée aux administrateurs."
                />
            </div>
        );
    }

    if (!currentCmpEdt) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Sélectionne d'abord une compétition dans la sidebar."/>
            </div>
        );
    }

    const handleSave = async (match: IMatch, side: Side, value: number) => {
        const matchteam_idt = side === 'DOM' ? match.MatchTeam_Idt_Dom : match.MatchTeam_Idt_Ext;
        const teamName = side === 'DOM' ? match.Nom_Dom : match.Nom_Ext;
        const key = `${match.Idt}-${side}`;
        setSavingKey(key);
        try {
            await AxiosServices.UpdateMatch(match, matchteam_idt, value);
            setMatches(prev => prev.map(m => {
                if (m.Idt !== match.Idt) return m;
                return side === 'DOM'
                    ? {...m, Score_Dom: value}
                    : {...m, Score_Ext: value};
            }));
            message.success(`Match n°${match.Num} — ${teamName} : ${value} enregistré.`, 1.5);
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.message ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} pour ${teamName} : ${serverMsg ?? 'erreur inconnue'}`);
            console.error('UpdateMatch error', err?.response ?? err);
        } finally {
            setSavingKey(null);
        }
    };

    const handleToggleFinished = async (match: IMatch, finished: boolean) => {
        setTogglingFinishedIdt(match.Idt);
        try {
            await AxiosServices.adminUpdateMatchMeta(match.Idt, {finished});
            setMatches(prev => prev.map(m => (m.Idt === match.Idt ? {...m, finished} : m)));
            message.success(`Match n°${match.Num} — ${finished ? 'marqué terminé' : 'remis en cours'}.`, 1.5);
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setTogglingFinishedIdt(null);
        }
    };

    const handleDelete = async (match: IMatch) => {
        setDeletingIdt(match.Idt);
        try {
            await AxiosServices.DeleteMatchScores(match.Idt);
            setMatches(prev => prev.map(m => (
                m.Idt === match.Idt
                    ? {...m, Score_Dom: -1, Score_Ext: -1}
                    : m
            )));
            message.success(`Match n°${match.Num} — scores supprimés.`, 1.5);
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.message ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} suppression match n°${match.Num} : ${serverMsg ?? 'erreur inconnue'}`);
            console.error('DeleteMatchScores error', err?.response ?? err);
        } finally {
            setDeletingIdt(null);
        }
    };

    const teamColor = (match: IMatch, side: Side): string => {
        const dom = match.Score_Dom ?? -1;
        const ext = match.Score_Ext ?? -1;
        if (dom === -1 || ext === -1) return '#ffffff';
        if (dom === ext) return '#1677ff';
        const wonDom = dom > ext;
        const isWinner = side === 'DOM' ? wonDom : !wonDom;
        return isWinner ? '#52c41a' : '#ff4d4f';
    };

    const columns = [
        {
            title: 'N°',
            dataIndex: 'Num',
            key: 'Num',
            width: 60,
            sorter: (a: IMatch, b: IMatch) => (a.Num ?? 0) - (b.Num ?? 0),
            defaultSortOrder: 'ascend' as const,
        },
        {
            title: 'Date',
            key: 'date',
            render: (_: any, m: IMatch) => {
                const d = matchAsUtcDate(m.Dte, m.Hre);
                return d ? formatMatchDateTime(d) : `${m.Dte ?? ''} ${m.Hre ?? ''}`.trim();
            },
            sorter: (a: IMatch, b: IMatch) => {
                const ta = matchAsUtcDate(a.Dte, a.Hre)?.getTime() ?? 0;
                const tb = matchAsUtcDate(b.Dte, b.Hre)?.getTime() ?? 0;
                return ta - tb;
            },
        },
        {
            title: 'Domicile',
            dataIndex: 'Nom_Dom',
            key: 'Nom_Dom',
            align: 'right' as const,
            render: (name: string, m: IMatch) => (
                <span style={{color: teamColor(m, 'DOM'), fontWeight: 500}}>{name}</span>
            ),
        },
        {
            title: 'Score',
            key: 'score',
            align: 'center' as const,
            render: (_: any, m: IMatch) => {
                const inputsDisabled = !demoMode && !HasMatchStarted(m);
                return (
                    <Tooltip title={inputsDisabled ? "Match pas encore commencé — activer Démo pour saisir quand même." : undefined}>
                        <Space size={6}>
                            <ScoreCell
                                match={m}
                                side="DOM"
                                saving={savingKey === `${m.Idt}-DOM`}
                                disabled={inputsDisabled}
                                onSave={handleSave}
                            />
                            <span style={{color: '#888'}}>—</span>
                            <ScoreCell
                                match={m}
                                side="EXT"
                                saving={savingKey === `${m.Idt}-EXT`}
                                disabled={inputsDisabled}
                                onSave={handleSave}
                            />
                        </Space>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Extérieur',
            dataIndex: 'Nom_Ext',
            key: 'Nom_Ext',
            render: (name: string, m: IMatch) => (
                <span style={{color: teamColor(m, 'EXT'), fontWeight: 500}}>{name}</span>
            ),
        },
        {
            title: 'Statut',
            key: 'status',
            render: (_: any, m: IMatch) => {
                const finished = !!Number(m.finished ?? 0);
                const started = HasMatchStarted(m);
                if (finished) return <Tag color="green">Terminé</Tag>;
                if (started) return <Tag color="orange">En cours</Tag>;
                return <Tag>À venir</Tag>;
            },
            filters: [
                {text: 'Terminé', value: 'done'},
                {text: 'En cours', value: 'live'},
                {text: 'À venir', value: 'future'},
            ],
            onFilter: (val: any, m: IMatch) => {
                const finished = !!Number(m.finished ?? 0);
                const started = HasMatchStarted(m);
                if (val === 'done') return finished;
                if (val === 'live') return started && !finished;
                if (val === 'future') return !started;
                return true;
            },
        },
        {
            title: 'Terminé',
            key: 'finished',
            align: 'center' as const,
            width: 90,
            render: (_: any, m: IMatch) => {
                const finished = !!Number(m.finished ?? 0);
                return (
                    <Switch
                        size="small"
                        checked={finished}
                        loading={togglingFinishedIdt === m.Idt}
                        onChange={(checked) => handleToggleFinished(m, checked)}
                    />
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center' as const,
            width: 90,
            render: (_: any, m: IMatch) => {
                const hasScore = (m.Score_Dom ?? -1) !== -1 || (m.Score_Ext ?? -1) !== -1;
                const started = HasMatchStarted(m);
                const canDelete = hasScore && !started;
                const tooltip = started
                    ? "Match déjà commencé — suppression interdite, seule la modification reste possible."
                    : (!hasScore ? "Aucun score à supprimer." : undefined);
                return (
                    <Popconfirm
                        title="Supprimer les scores"
                        description={`Effacer les scores du match n°${m.Num} (${m.Nom_Dom} — ${m.Nom_Ext}) ?`}
                        okText="Supprimer"
                        okButtonProps={{danger: true}}
                        cancelText="Annuler"
                        onConfirm={() => handleDelete(m)}
                        disabled={!canDelete || deletingIdt === m.Idt}
                    >
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined/>}
                            disabled={!canDelete}
                            loading={deletingIdt === m.Idt}
                            title={tooltip}
                        />
                    </Popconfirm>
                );
            },
        },
    ];

    return (
        <div style={{padding: 16, height: '100%', boxSizing: 'border-box', overflow: 'hidden', background: ACCENT}}>
            <Card
                styles={{
                    header: {background: ACCENT, color: '#fff'},
                    body: {background: ACCENT, color: '#fff'},
                }}
                style={{background: ACCENT, borderColor: ACCENT}}
                title={
                    <Space>
                        <SaveOutlined/>
                        <span>Administration — Saisie des scores</span>
                    </Space>
                }
            >
                <Paragraph style={{marginBottom: 16, color: '#cfd8d3'}}>
                    Sortir d'une cellule (Tab ou clic ailleurs) enregistre automatiquement le score modifié.
                    Par défaut, seuls les matchs déjà commencés sont saisissables ; activer
                    « Démo » pour outrepasser cette règle.
                </Paragraph>
                <Space style={{marginBottom: 16}} wrap>
                    <Button
                        type={showOld ? 'primary' : 'default'}
                        icon={<HistoryOutlined/>}
                        onClick={() => setShowOld(v => !v)}
                    >
                        Inclure les anciens matchs
                    </Button>
                    <Button
                        type={showNoScore ? 'primary' : 'default'}
                        icon={<QuestionCircleOutlined/>}
                        onClick={() => setShowNoScore(v => !v)}
                    >
                        Sans score saisi
                    </Button>
                    <Button
                        type={hideFinished ? 'primary' : 'default'}
                        onClick={() => setHideFinished(v => !v)}
                    >
                        Cacher les matchs terminés
                    </Button>
                    <Tooltip title="Permet de saisir un score même quand le match n'a pas encore commencé.">
                        <Space size={6}>
                            <Switch
                                checked={demoMode}
                                onChange={setDemoMode}
                                checkedChildren="Démo"
                                unCheckedChildren="Démo"
                            />
                        </Space>
                    </Tooltip>
                </Space>
                <ConfigProvider
                    theme={{algorithm: theme.darkAlgorithm}}
                >
                    <Table
                        dataSource={filteredMatches}
                        columns={columns}
                        rowKey={(m) => m.Idt.toString()}
                        loading={loading}
                        pagination={false}
                        size="small"
                        sticky
                        scroll={{y: 'calc(100vh - 410px)'}}
                    />
                </ConfigProvider>
            </Card>
        </div>
    );
};

export default AdminScores;
